import type { Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { ExternalLink } from "@src/components/ExternalLink";
import { activationCameraKey, type SignalDetail } from "@visioni/a3s-monitoring/app";
import { EMPTY, formatPreciseInstant, formatText, hasText } from "../format";
import { Field, InlineLink, Section } from "./DetailSection";
import { SignalProcessingFields } from "./SignalProcessing";
import { TimelineValue } from "./TimelineValue";

interface SignalDetailViewProps {
  readonly signal: SignalDetail;
  readonly tenant: { readonly label: string; readonly href?: Href };
  readonly clientHref: (clientNo: string) => Href;
  readonly activationHref: (ticketNo: string) => Href;
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
  /** Omit where the camera page cannot be reached; the camera then shows unlinked. */
  readonly cameraHref?: (siteId: string, cameraKey: string) => Href;
  /** Omit where there is no tenant list to narrow; site and camera then show unlinked. */
  readonly listHref?: (filters: { site: string; camera?: string }) => Href;
}

const numberOrEmpty = (value: number | undefined) => (value === undefined ? EMPTY : String(value));

/**
 * One Patriot signal: what it is and where it came from, what Patriot holds
 * on it, and — when a3s sent it — the clip and how a3s handled it.
 */
export function SignalDetailView({
  signal,
  tenant,
  clientHref,
  activationHref,
  siteLabel,
  cameraLabel,
  cameraHref,
  listHref,
}: SignalDetailViewProps) {
  const cameraKey = activationCameraKey(signal);
  const site = signal.a3sSite;

  return (
    <View gap="4xl">
      <Section title="Summary">
        <Field label="Signal ID">{signal.signalId}</Field>
        <Field label="Received">{formatPreciseInstant(signal.receivedDateTime)}</Field>
        <Field label="Description">{formatText(signal.description)}</Field>
        <Field label="Type">{numberOrEmpty(signal.typeNo)}</Field>
        <Field label="Alarm type">{formatText(signal.alarmType)}</Field>
        <Field label="Source">{formatText(signal.source)}</Field>
        <Field label="Client">
          {signal.clientNo !== undefined ?
            <InlineLink href={clientHref(signal.clientNo)}>
              {hasText(signal.clientName) ? `${signal.clientNo} · ${signal.clientName}` : signal.clientNo}
            </InlineLink>
          : EMPTY}
        </Field>
        <Field label="Activation">
          {signal.ticketNo !== undefined ?
            <InlineLink href={activationHref(signal.ticketNo)}>{signal.ticketNo}</InlineLink>
          : EMPTY}
        </Field>
      </Section>

      <Section title="a3s">
        <Field label="Organisation">
          {tenant.href !== undefined ?
            <InlineLink href={tenant.href}>{tenant.label}</InlineLink>
          : tenant.label}
        </Field>
        <Field label="Site">
          {site === undefined ?
            EMPTY
          : listHref !== undefined ?
            <InlineLink href={listHref({ site })}>{siteLabel(site)}</InlineLink>
          : siteLabel(site)}
        </Field>
        <Field label="Camera">
          {site !== undefined && cameraKey !== undefined ?
            <View flexDirection="row" gap="lg" flexWrap="wrap">
              {cameraHref !== undefined ?
                <InlineLink href={cameraHref(site, cameraKey)}>{cameraLabel(cameraKey)}</InlineLink>
              : <Text textStyle="sm/regular" color="text" selectable>
                  {cameraLabel(cameraKey)}
                </Text>
              }
              {listHref !== undefined && (
                <InlineLink href={listHref({ site, camera: cameraKey })}>Other signals for this camera</InlineLink>
              )}
            </View>
          : EMPTY}
        </Field>
        <Field label="a3s signal ID">{formatText(signal.a3sSignalId)}</Field>
        {/* The key, not the URL: the signed query string is noise, and the key is what AWS tooling takes. */}
        <Field label="Clip">
          {signal.a3sClip !== undefined ?
            <ExternalLink href={signal.a3sClip.url} style={styles.inlineLink}>
              <Text textStyle="sm/regular" style={styles.link}>
                {signal.a3sClip.key}
              </Text>
            </ExternalLink>
          : EMPTY}
        </Field>
      </Section>

      {signal.a3sProcessing !== undefined && (
        <Section title="a3s processing">
          <SignalProcessingFields processing={signal.a3sProcessing} />
        </Section>
      )}

      <Section title="Patriot record">
        {/* Free text; on an a3s signal it holds the marker the a3s signal ID is read from. */}
        <Field label="Notes">{formatText(signal.notes)}</Field>
        <Field label="Module">{numberOrEmpty(signal.moduleNo)}</Field>
        <Field label="Zone">{numberOrEmpty(signal.zoneNumber)}</Field>
        <Field label="User">{numberOrEmpty(signal.userNumber)}</Field>
        <Field label="Previous status">{formatText(signal.previousStatus)}</Field>
        <Field label="Current status">{formatText(signal.currentStatus)}</Field>
        <Field label="Media">{formatText(signal.media)}</Field>
        <Field label="Raw data">{formatText(signal.rawData)}</Field>
      </Section>

      {/* The gap from received to first copied is how far the replica lagged Patriot. */}
      <Section title="Copy in a3s">
        <Field label="First copied">
          <TimelineValue from={signal.receivedDateTime} at={signal.a3sFirstSeenAt} sinceLabel="after received" />
        </Field>
        <Field label="Last changed">
          <TimelineValue from={signal.receivedDateTime} at={signal.a3sLastWrittenAt} sinceLabel="after received" />
        </Field>
      </Section>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  inlineLink: { alignSelf: "flex-start" },
  link: { color: theme.colors.interactive.buttons.secondary_foreground, textDecorationLine: "underline" },
}));
